/** Virtual Try-On media preview presentation — active vs stale vs in-flight runs.
 *
 * Overwrite contract: old current moves to History immediately; Current Result
 * enters GENERATING (placeholder). Old image must not keep reading as
 * COMPLETED current. Spec prefers History + generating placeholder over
 * lingering previous* as the effective current result.
 */

export const TRYON_EXECUTION_STATUSES = new Set(['SUBMITTING', 'QUEUED', 'PROCESSING'])

export type TryOnPresentationMode =
  | 'EMPTY'
  | 'READY_TO_GENERATE'
  | 'COMPLETED_CURRENT'
  | 'COMPLETED_STALE'
  | 'SUBMITTING'
  | 'QUEUED'
  | 'PROCESSING'
  | 'FAILED'

export interface TryOnPreviewPresentation {
  mode: TryOnPresentationMode
  showActiveResult: boolean
  showStaleResult: boolean
  showGenerating: boolean
  previewOutputAssetId: string | null
  previewGeneratedOutputId: string | null
}

export function isTryOnExecutionActive(status: string | undefined): boolean {
  return TRYON_EXECUTION_STATUSES.has(status ?? '')
}

export function hasTryOnPersistedOutput(
  outputAssetId?: string | null,
  generatedOutputId?: string | null,
): boolean {
  return Boolean(outputAssetId || generatedOutputId)
}

function resolveExecutionMode(status: string): TryOnPresentationMode {
  if (status === 'SUBMITTING') return 'SUBMITTING'
  if (status === 'QUEUED') return 'QUEUED'
  return 'PROCESSING'
}

function pickPreviewIds(args: {
  outputAssetId: string | null
  generatedOutputId: string | null
  previousOutputAssetId: string | null
  previousGeneratedOutputId: string | null
  /** When true (overwrite lifecycle), never fall back to previous* as current preview. */
  preferPlaceholder?: boolean
}): { previewOutputAssetId: string | null; previewGeneratedOutputId: string | null } {
  if (args.outputAssetId || args.generatedOutputId) {
    return {
      previewOutputAssetId: args.outputAssetId,
      previewGeneratedOutputId: args.generatedOutputId,
    }
  }
  if (args.preferPlaceholder) {
    return { previewOutputAssetId: null, previewGeneratedOutputId: null }
  }
  return {
    previewOutputAssetId: args.previousOutputAssetId,
    previewGeneratedOutputId: args.previousGeneratedOutputId,
  }
}

/** Pure presentation resolver — single source of truth for Try-On preview media. */
export function resolveTryOnPreviewPresentation(args: {
  status: string
  needsRegeneration: boolean
  outputAssetId?: string | null
  generatedOutputId?: string | null
  previousOutputAssetId?: string | null
  previousGeneratedOutputId?: string | null
  runHydrating?: boolean
  /** Overwrite path: show generating placeholder, not old completed image. */
  preferGeneratingPlaceholder?: boolean
}): TryOnPreviewPresentation {
  const status = args.status || 'WAITING'
  const needsRegeneration = Boolean(args.needsRegeneration)
  const outputAssetId = args.outputAssetId ?? null
  const generatedOutputId = args.generatedOutputId ?? null
  const previousOutputAssetId = args.previousOutputAssetId ?? null
  const previousGeneratedOutputId = args.previousGeneratedOutputId ?? null
  const preferPlaceholder = Boolean(args.preferGeneratingPlaceholder)
  const hasOutput = hasTryOnPersistedOutput(outputAssetId, generatedOutputId)
  const hasPrevious = hasTryOnPersistedOutput(previousOutputAssetId, previousGeneratedOutputId)
  const hasStableCompletedPresentation = status === 'COMPLETED' && hasOutput
  const executing =
    isTryOnExecutionActive(status) ||
    (Boolean(args.runHydrating) && !hasStableCompletedPresentation)

  if (executing) {
    const preview = pickPreviewIds({
      outputAssetId,
      generatedOutputId,
      previousOutputAssetId,
      previousGeneratedOutputId,
      preferPlaceholder,
    })
    const hasPreview = Boolean(preview.previewOutputAssetId || preview.previewGeneratedOutputId)
    return {
      mode: resolveExecutionMode(status),
      showActiveResult: false,
      showStaleResult: hasPreview,
      showGenerating: true,
      previewOutputAssetId: preview.previewOutputAssetId,
      previewGeneratedOutputId: preview.previewGeneratedOutputId,
    }
  }

  if (status === 'FAILED') {
    // Failed current stays failed — do not silently resurrect previous as COMPLETED.
    // Optional weak previous preview under failed UI only when explicitly kept in previous*.
    const preview = pickPreviewIds({
      outputAssetId,
      generatedOutputId,
      previousOutputAssetId,
      previousGeneratedOutputId,
      preferPlaceholder: true,
    })
    const hasPreview = Boolean(preview.previewOutputAssetId || preview.previewGeneratedOutputId)
    return {
      mode: 'FAILED',
      showActiveResult: false,
      showStaleResult: hasPreview,
      showGenerating: false,
      previewOutputAssetId: preview.previewOutputAssetId,
      previewGeneratedOutputId: preview.previewGeneratedOutputId,
    }
  }

  if (status === 'COMPLETED' && hasOutput && !needsRegeneration) {
    return {
      mode: 'COMPLETED_CURRENT',
      showActiveResult: true,
      showStaleResult: false,
      showGenerating: false,
      previewOutputAssetId: outputAssetId,
      previewGeneratedOutputId: generatedOutputId,
    }
  }

  if (status === 'COMPLETED' && hasOutput && needsRegeneration) {
    return {
      mode: 'COMPLETED_STALE',
      showActiveResult: false,
      showStaleResult: true,
      showGenerating: false,
      previewOutputAssetId: outputAssetId,
      previewGeneratedOutputId: generatedOutputId,
    }
  }

  if (hasOutput) {
    return {
      mode: 'READY_TO_GENERATE',
      showActiveResult: false,
      showStaleResult: false,
      showGenerating: false,
      previewOutputAssetId: null,
      previewGeneratedOutputId: null,
    }
  }

  if (hasPrevious) {
    return {
      mode: 'READY_TO_GENERATE',
      showActiveResult: false,
      showStaleResult: true,
      showGenerating: false,
      previewOutputAssetId: previousOutputAssetId,
      previewGeneratedOutputId: previousGeneratedOutputId,
    }
  }

  return {
    mode: status === 'WAITING' || status === 'READY' ? 'EMPTY' : 'READY_TO_GENERATE',
    showActiveResult: false,
    showStaleResult: false,
    showGenerating: false,
    previewOutputAssetId: null,
    previewGeneratedOutputId: null,
  }
}

/** Legacy: stash current into previous* (kept for reconcile paths). Prefer overwrite lifecycle. */
export function buildTryOnRunAcceptancePatch(fields: {
  outputAssetId?: string | null
  generatedOutputId?: string | null
}): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  if (fields.outputAssetId) patch.previousOutputAssetId = fields.outputAssetId
  if (fields.generatedOutputId) patch.previousGeneratedOutputId = fields.generatedOutputId
  if (fields.outputAssetId || fields.generatedOutputId) {
    patch.outputAssetId = null
    patch.generatedOutputId = null
  }
  return patch
}

export type TryOnHistoryEntry = {
  version: number
  outputAssetId: string | null
  generatedOutputId: string | null
  outputWidth?: number | null
  outputHeight?: number | null
  completedAt?: string
  personAssetId?: string | null
  garmentAssetId?: string | null
  providerOptionId?: string | null
  quality?: string | null
  outfitScope?: string | null
  category?: string | null
  prompt?: string | null
  jobId?: string | null
  runId?: string | null
  inputFingerprint?: string | null
}

/** Append the outgoing current result into generationHistory before replacing it. */
export function appendTryOnHistoryEntry(
  history: TryOnHistoryEntry[],
  entry: Omit<TryOnHistoryEntry, 'version'> & { version?: number },
): TryOnHistoryEntry[] {
  if (!entry.outputAssetId && !entry.generatedOutputId) return history
  const nextVersion = entry.version ?? (history.reduce((max, h) => Math.max(max, h.version), 0) + 1)
  return [
    ...history,
    {
      version: nextVersion,
      outputAssetId: entry.outputAssetId ?? null,
      generatedOutputId: entry.generatedOutputId ?? null,
      outputWidth: entry.outputWidth ?? null,
      outputHeight: entry.outputHeight ?? null,
      completedAt: entry.completedAt ?? new Date().toISOString(),
      personAssetId: entry.personAssetId ?? null,
      garmentAssetId: entry.garmentAssetId ?? null,
      providerOptionId: entry.providerOptionId ?? null,
      quality: entry.quality ?? null,
      outfitScope: entry.outfitScope ?? null,
      category: entry.category ?? null,
      prompt: entry.prompt ?? null,
      jobId: entry.jobId ?? null,
      runId: entry.runId ?? entry.jobId ?? null,
      inputFingerprint: entry.inputFingerprint ?? null,
    },
  ].slice(-20)
}

export type TryOnOverwriteContext = {
  outputAssetId?: string | null
  generatedOutputId?: string | null
  outputWidth?: number | null
  outputHeight?: number | null
  generationHistory?: TryOnHistoryEntry[]
  personAssetId?: string | null
  garmentAssetId?: string | null
  providerOptionId?: string | null
  quality?: string | null
  outfitScope?: string | null
  category?: string | null
  prompt?: string | null
  jobId?: string | null
  inputFingerprint?: string | null
}

/**
 * Overwrite confirmed: old current → History immediately; clear current/previous;
 * enter SUBMITTING so UI shows generating placeholder (not old completed).
 */
export function buildTryOnOverwriteLifecyclePatch(ctx: TryOnOverwriteContext): Record<string, unknown> {
  const history = Array.isArray(ctx.generationHistory) ? ctx.generationHistory : []
  const nextHistory = appendTryOnHistoryEntry(history, {
    outputAssetId: ctx.outputAssetId ?? null,
    generatedOutputId: ctx.generatedOutputId ?? null,
    outputWidth: ctx.outputWidth ?? null,
    outputHeight: ctx.outputHeight ?? null,
    personAssetId: ctx.personAssetId ?? null,
    garmentAssetId: ctx.garmentAssetId ?? null,
    providerOptionId: ctx.providerOptionId ?? null,
    quality: ctx.quality ?? null,
    outfitScope: ctx.outfitScope ?? null,
    category: ctx.category ?? null,
    prompt: ctx.prompt ?? null,
    jobId: ctx.jobId ?? null,
    inputFingerprint: ctx.inputFingerprint ?? null,
  })
  return {
    generationHistory: nextHistory,
    outputAssetId: null,
    generatedOutputId: null,
    previousOutputAssetId: null,
    previousGeneratedOutputId: null,
    // Drop previous job pointer so a failed overwrite cannot re-hydrate the
    // prior succeeded run as current COMPLETED after refresh.
    jobId: null,
    needsRegeneration: false,
    status: 'SUBMITTING',
    errorMessage: null,
  }
}
